import { Link } from 'react-router-dom'
import { Mail, User, Calendar, Lock, Save } from 'lucide-react'
import { AppCard } from '@/features/common/components/AppCard'
import { LoadingSpinner } from '@/features/common/components/LoadingSpinner'
import { extractRtkErrorMessage } from '@/services/apiErrors'
import { useAdminProfileViewModel } from '../viewmodels/use_admin_profile_viewmodel'

function formatDate(value?: string) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

function profileInitials(name: string, email: string) {
  const source = name.trim() || email
  const parts = source.split(/\s+/).filter(Boolean)
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return source.slice(0, 2).toUpperCase()
}

export function AdminProfilePageView() {
  const {
    profile,
    isLoading,
    loadError,
    name,
    formError,
    isSaving,
    handleNameChange,
    handleReset,
    handleSubmit,
    refetch,
  } = useAdminProfileViewModel()

  if (isLoading) {
    return (
      <div className="flex min-h-[16rem] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (loadError && !profile) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900 sm:text-3xl">
          Admin profile
        </h1>
        <AppCard>
          <p className="font-medium text-red-600">Could not load your profile</p>
          <p className="mt-2 text-sm text-gray-500">{extractRtkErrorMessage(loadError)}</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="btn btn-primary mt-4 rounded-pill px-4 py-2 text-sm"
          >
            Try again
          </button>
        </AppCard>
      </div>
    )
  }

  const email = profile?.email ?? ''
  const displayName = profile?.name ?? 'Admin'

  return (
    <div className="space-y-8">
      <ProfilePageHeader />
      <ProfilePageGrid
        profile={profile}
        email={email}
        displayName={displayName}
        name={name}
        formError={formError}
        isSaving={isSaving}
        onNameChange={handleNameChange}
        onReset={handleReset}
        onSubmit={handleSubmit}
      />
    </div>
  )
}

function ProfilePageHeader() {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900 sm:text-3xl">
          Admin profile
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          View and update your account. Email cannot be changed here.
        </p>
      </div>
      <Link
        to="/admin/settings/password"
        className="btn btn-secondary inline-flex items-center justify-center gap-2 rounded-pill px-4 py-2 text-sm"
      >
        <Lock className="h-4 w-4" />
        Change password
      </Link>
    </div>
  )
}

function ProfilePageGrid(props: {
  profile: ReturnType<typeof useAdminProfileViewModel>['profile']
  email: string
  displayName: string
  name: string
  formError: string | null
  isSaving: boolean
  onNameChange: (value: string) => void
  onReset: () => void
  onSubmit: (e: React.FormEvent) => void
}) {
  const {
    profile,
    email,
    displayName,
    name,
    formError,
    isSaving,
    onNameChange,
    onReset,
    onSubmit,
  } = props

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <AppCard className="lg:col-span-1">
        <div className="flex flex-col items-center text-center sm:flex-row sm:items-start sm:gap-5 sm:text-left">
          <ProfileAvatar displayName={displayName} email={email} />
          <div className="mt-4 min-w-0 sm:mt-0">
            <p className="truncate text-lg font-semibold text-gray-900">{displayName}</p>
            <p className="mt-1 truncate text-sm text-gray-500">{email}</p>
            <p className="mt-3 inline-flex rounded-pill bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
              Administrator
            </p>
          </div>
        </div>
        <dl className="mt-6 space-y-3 border-t border-gray-100 pt-6 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-gray-500">Member since</dt>
            <dd className="text-right font-medium text-gray-900">
              {formatDate(profile?.createdAt)}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-gray-500">Last updated</dt>
            <dd className="text-right font-medium text-gray-900">
              {formatDate(profile?.updatedAt)}
            </dd>
          </div>
        </dl>
      </AppCard>

      <AppCard className="lg:col-span-2">
        <h2 className="text-lg font-semibold text-gray-900">Account details</h2>
        <p className="mt-1 text-sm text-gray-500">
          Only your display name can be updated via this form.
        </p>

        <form onSubmit={onSubmit} className="mt-6 space-y-5">
          <div>
            <label
              htmlFor="admin-email"
              className="mb-1.5 flex items-center gap-2 text-sm font-medium text-gray-700"
            >
              <Mail className="h-4 w-4 text-gray-400" />
              Email
            </label>
            <input
              id="admin-email"
              type="email"
              value={email}
              readOnly
              disabled
              className="w-full cursor-not-allowed rounded-shell-inner border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-600"
            />
            <p className="mt-1.5 text-xs text-gray-400">Email is not mutable on this endpoint.</p>
          </div>

          <div>
            <label
              htmlFor="admin-name"
              className="mb-1.5 flex items-center gap-2 text-sm font-medium text-gray-700"
            >
              <User className="h-4 w-4 text-gray-400" />
              Display name
            </label>
            <input
              id="admin-name"
              type="text"
              value={name}
              onChange={e => onNameChange(e.target.value)}
              placeholder="Your name"
              className="w-full rounded-shell-inner border border-gray-200 px-4 py-2.5 text-sm text-gray-900 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
              autoComplete="name"
            />
          </div>

          {profile?.id && (
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <Calendar className="h-3.5 w-3.5" />
              <span>Account ID: {profile.id}</span>
            </div>
          )}

          {formError && (
            <p className="text-sm font-medium text-red-600" role="alert">
              {formError}
            </p>
          )}

          <div className="flex flex-wrap gap-3 pt-2">
            <button
              type="submit"
              disabled={isSaving}
              className="btn btn-primary inline-flex items-center justify-center gap-2 rounded-pill px-5 py-2.5 text-sm disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <LoadingSpinner size="sm" />
                  Saving…
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save changes
                </>
              )}
            </button>
            <button
              type="button"
              disabled={isSaving}
              onClick={onReset}
              className="btn btn-secondary rounded-pill px-5 py-2.5 text-sm"
            >
              Reset
            </button>
          </div>
        </form>
      </AppCard>
    </div>
  )
}

function ProfileAvatar({ displayName, email }: { displayName: string; email: string }) {
  return (
    <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-brand-muted text-xl font-semibold text-brand">
      {profileInitials(displayName, email)}
    </div>
  )
}
